// DOM ad placements: phone home banner, Farm featured card, Chirp sponsored post, loading sponsor,
// plus the ad card + "you are leaving" interstitial. Every ad carries a visible Ad/Sponsored label.
import { useEffect, useRef, useState } from "preact/hooks";
import type { ServedAd, SlotId } from "@degen/shared/ads";
import { adCard, adFor, click, impression } from "../../net/ads";

/** ≥50% visible for 1 s = one impression (§6.4). */
function useImpression(ad: ServedAd | null) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !ad) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.intersectionRatio >= 0.5) timer ??= setTimeout(() => impression(ad), 1000);
        else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [ad?.slot, ad?.creativeId]);
  return ref;
}

function Label({ ad }: { ad: ServedAd }) {
  return <span class="ad-label">{ad.house ? "Degen World" : ad.slot === "chirp.sponsored" ? "Sponsored" : "Ad"}</span>;
}

export function AdBanner({ slot, dismissible }: { slot: SlotId; dismissible?: boolean }) {
  const ad = adFor(slot);
  const ref = useImpression(ad);
  const [gone, setGone] = useState(() => {
    try {
      return sessionStorage.getItem(`ad-dismissed:${slot}`) === "1";
    } catch {
      return false;
    }
  });
  if (!ad || gone) return null;
  return (
    <div ref={ref} class={`ad-banner slot-${slot.replace(/\./g, "-")}`} role="complementary" aria-label="Advertisement">
      <button class="ad-body" onClick={() => click(ad)}>
        {ad.imageUrl ? (
          <img src={ad.imageUrl} alt={ad.title} loading="lazy" />
        ) : (
          <span class="ad-text">
            <b>{ad.title}</b> {ad.text}
          </span>
        )}
      </button>
      <Label ad={ad} />
      {dismissible && (
        <button
          class="ad-x"
          aria-label="Dismiss ad"
          onClick={() => {
            setGone(true);
            try {
              sessionStorage.setItem(`ad-dismissed:${slot}`, "1");
            } catch {
              /* ignore */
            }
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

/** Sponsored post for the Chirp feed (the feed inserts at most one per 12 posts). */
export function SponsoredPost() {
  const ad = adFor("chirp.sponsored");
  const ref = useImpression(ad);
  if (!ad || ad.house) return null; // house ads don't take feed space
  return (
    <li ref={ref as never} class="post sponsored pickable" onClick={() => click(ad)}>
      <span class="news-avatar">📣</span>
      <div class="post-body">
        <div class="post-head">
          <b>{ad.advertiser}</b>
          <span class="ad-label">Sponsored</span>
        </div>
        <p class="post-text">{ad.text || ad.title}</p>
        {ad.imageUrl && <img class="post-img" src={ad.imageUrl} alt="" loading="lazy" />}
      </div>
    </li>
  );
}

export function LoadingSponsor() {
  const ad = adFor("loading.sponsor");
  const ref = useImpression(ad);
  if (!ad || ad.house) return null;
  return (
    <div ref={ref} class="loading-sponsor">
      <span class="ad-label">Ad</span> presented by {ad.imageUrl ? <img src={ad.imageUrl} alt={ad.advertiser} /> : <b>{ad.advertiser}</b>}
    </div>
  );
}

/** Tapping any ad opens this card first; "Visit site" goes through the safety interstitial (§6.2). */
export function AdCardModal() {
  const ad = adCard.value;
  const [leaving, setLeaving] = useState(false);
  if (!ad) return null;
  const close = () => {
    adCard.value = null;
    setLeaving(false);
  };
  return (
    <div class="modal-backdrop" onClick={close}>
      <div class="modal ad-card" role="dialog" aria-label="Advertisement" onClick={(e) => e.stopPropagation()}>
        {!leaving ? (
          <>
            <div class="row-between">
              <b>{ad.advertiser}</b>
              <span class="ad-label">{ad.house ? "Degen World" : "Ad"}</span>
            </div>
            {ad.imageUrl && <img class="ad-card-img" src={ad.imageUrl} alt={ad.title} />}
            <h3>{ad.title}</h3>
            <p class="muted">{ad.text}</p>
            {ad.clickUrl ? (
              <button class="btn primary" onClick={() => setLeaving(true)}>
                Visit site
              </button>
            ) : null}
            <button class="link small" onClick={close}>
              Close
            </button>
          </>
        ) : (
          <>
            <h3>You are leaving Degen World</h3>
            <p>Nothing in this game uses real money. Never share a seed phrase or private key with anyone.</p>
            <p class="muted small">{ad.clickUrl}</p>
            <button
              class="btn primary"
              onClick={() => {
                window.open(ad.clickUrl!, "_blank", "noopener,noreferrer");
                close();
              }}
            >
              Continue to site
            </button>
            <button class="btn ghost" onClick={close}>
              Stay in the game
            </button>
          </>
        )}
      </div>
    </div>
  );
}
